import React, { useEffect, useRef, useState } from 'react';
import { Box, Heading } from '@landr/maestro';
import {
    BillingCommitment,
    Environment,
    Languages,
    PricingProvider,
    PricingTabs,
    PricingTabsEnum,
} from '@landr/pricing';
import { PageContextType } from 'types';
import { navigate } from 'gatsby';
import {
    addTrailingSlash,
    getDecodedPathname,
    getTwoLetterLanguageCode,
    pricingTabToUid,
    pricingUidToTab,
} from 'helpers';
import { ApplicationEnum } from '@landr/core.models';
import { getCheckoutLinkFromPlanCode, getProductCheckoutLinkFromProductCode } from 'helpers/pricingLinkUtils';
import { useAppContext } from 'context/AppContext';
import { analyticsV2, CtaGoal, CtaType, InteractionSource } from 'analytics';
import { FeatureFlagsEnum } from '../../../enum/featureFlags';
import { useFlag } from '../../../context/FlagsContext';
import { log } from '../../../log';
import { SupportedPrismicSlicesEnum } from '../enums';
import { useAuthentication } from '../../Authentication';
import { StyledPricingContentBox } from './styled';

const logger = log.getInstance('PricingTab');

export type PricingSliceProps = {
    pageContext: PageContextType;
    location: Location;
    slice: {
        type: SupportedPrismicSlicesEnum.pricing;
        primary: {
            /* eslint-disable @typescript-eslint/naming-convention */
            pricing_title: string;
        };
    };
};

const updatePath = (nextTab: PricingTabsEnum, language: Languages, pageContext: PageContextType) => {
    const nextTabInCurrentLanguage = pricingTabToUid[nextTab][language];
    const path = `${language}/${pageContext.uid}/${nextTabInCurrentLanguage}`;
    navigate(path);
    return path;
};

const getAnalyticsCtaText = (nextTab: PricingTabsEnum) => {
    switch (nextTab) {
        case PricingTabsEnum.Bundle:
            return 'LANDR Studio';
        case PricingTabsEnum.Mastering:
            return 'Mastering';
        case PricingTabsEnum.Distribution:
            return 'Distribution';
        case PricingTabsEnum.Samples:
            return 'Samples';
        case PricingTabsEnum.Chromatic:
            return 'Chromatic';
        case PricingTabsEnum.Sessions:
            return 'Sessions';
        default:
            return nextTab;
    }
};

export const getLocationTabUid = (location: Location): string | undefined => {
    const decodedLocation = addTrailingSlash(getDecodedPathname(location.pathname));
    return Object.keys(pricingUidToTab).find((pricingUid) => decodedLocation.includes(`/${pricingUid}/`));
};

const PricingTabToHomePageEnum = {
    [PricingTabsEnum.Mastering]: 'Mastering',
    [PricingTabsEnum.Distribution]: 'Distribution',
    [PricingTabsEnum.Sessions]: 'Sessions',
    [PricingTabsEnum.Samples]: 'Samples',
    [PricingTabsEnum.Bundle]: null,
    [PricingTabsEnum.Chromatic]: 'Chromatic',
};

function getSelectedTab(location: Location): PricingTabsEnum {
    getLocationTabUid(location);
    const tabUid = getLocationTabUid(location);
    return tabUid ? pricingUidToTab[tabUid as PricingTabsEnum] : PricingTabsEnum.Bundle;
}

export const Pricing: React.FC<PricingSliceProps> = ({ pageContext, location, slice }) => {
    /* eslint-disable @typescript-eslint/naming-convention */
    const { pricing_title } = slice.primary;
    const language = getTwoLetterLanguageCode(pageContext.lang) as Languages;
    const [selectedTab, setSelectedTab] = useState(() => getSelectedTab(location));
    const { signupUrl, isAuthenticated } = useAuthentication();
    const { setHomePage, resetHomePage, homePage } = useAppContext();
    const modalHeaderRef = useRef<HTMLDivElement>() as React.RefObject<HTMLDivElement>;

    const isSamplesCreditIncreaseEnabled = useFlag([FeatureFlagsEnum.StudioSamplesCreditIncrease]);
    const isDistributionNewPricingEnabled = useFlag([FeatureFlagsEnum.DistributionNewPricing]);
    const isChromaticToPluginsEnabled = useFlag([FeatureFlagsEnum.ChromaticToPlugins]);

    const handleSubscriptionPurchase = ({
        code,
        commitment,
        couponCode,
    }: {
        code: string;
        commitment: BillingCommitment;
        couponCode?: string;
    }) => {
        typeof window !== 'undefined' &&
            window.open(
                getCheckoutLinkFromPlanCode({
                    code,
                    commitment,
                    couponCode,
                    homePage,
                    currentTab: selectedTab,
                    language,
                }),
                '_self',
            );
    };

    const handleOnSignUp = () => {
        typeof window !== 'undefined' && window.open(signupUrl, '_self');
    };

    const handleProductPurchase = ({ code }: { code: string }) => {
        typeof window !== 'undefined' &&
            window.open(
                getProductCheckoutLinkFromProductCode({ code, homePage, currentTab: selectedTab, language }),
                '_self',
            );
    };
    const updateHomePageFromTabValue = (nextTab: PricingTabsEnum) => {
        const homePage = PricingTabToHomePageEnum[nextTab];
        if (homePage) {
            setHomePage(homePage);
        } else {
            resetHomePage();
        }
    };
    const handleTabSelected = (nextTab: PricingTabsEnum) => {
        const destinationPath = updatePath(nextTab, language, pageContext);
        setSelectedTab(nextTab);
        updateHomePageFromTabValue(nextTab);

        analyticsV2.trackCTAClicked({
            ctaGoal: CtaGoal.ViewPricing,
            ctaType: CtaType.Button,
            interactionSource: InteractionSource.PricingNav,
            ctaText: getAnalyticsCtaText(nextTab),
            ctaDestination: destinationPath,
        });
    };

    useEffect(() => {
        const selectedTab = getSelectedTab(location);
        setSelectedTab(selectedTab);
        updateHomePageFromTabValue(selectedTab);
    }, [location]);

    return (
        <PricingProvider
            applicationName={ApplicationEnum.GuestSite}
            env={process.env.GATSBY_ENV as Environment}
            language={language}
            log={logger}
            locationPathname={location.pathname}
            onSubscriptionPurchase={handleSubscriptionPurchase}
            onProductPurchase={handleProductPurchase}
            onSignUp={handleOnSignUp}
        >
            <StyledPricingContentBox>
                <Box maxWidth="1200px" width="100%">
                    <Box flexDirection="column" alignSelf="flex-center" ref={modalHeaderRef}>
                        <Box alignSelf="flex-start" width="100%">
                            <Heading as="h1" size="jb">
                                {pricing_title}
                            </Heading>
                        </Box>
                    </Box>
                    <PricingTabs
                        alcRemovalExperimentId={
                            process.env.GATSBY_OPTIMIZE_MASTERING_PRICING_ALC_REMOVAL_EXPERIMENT as string
                        }
                        selectedTab={selectedTab}
                        onSelectTab={handleTabSelected}
                        isSessionsFreePlanDisabled={false}
                        isMasteringV2User={true}
                        isStudioSampleCreditIncreaseEnabled={isSamplesCreditIncreaseEnabled}
                        isDistributionNewPricingEnabled={isDistributionNewPricingEnabled}
                        isPluginsSubscriptionEnabled={isChromaticToPluginsEnabled}
                        isLoggedIn={isAuthenticated}
                        modalHeaderRef={modalHeaderRef}
                    />
                </Box>
            </StyledPricingContentBox>
        </PricingProvider>
    );
};
